import { createSelector, createStructuredSelector } from 'reselect';
import { initialState } from './reducer';
import { RootState, Selected } from '../../interface';
import { Gigakombi, NameSpace, ProductType } from '../../enum';
import { ProductConfig } from './interface';
import { selectCustomer } from '@vfde-sails/login';
import {
    JarvisMarketability,
    JarvisProduct,
    JarvisProviderType,
    selectAvailabilityResult,
    selectMarketability,
    selectTvConnect,
    selectTvConnectBroadvision,
} from '@vfde-sails/availability';

const getMetacodeBySelected = (config: ProductConfig, selected: Selected, marketability?: JarvisMarketability) => {
    const addressIsUnity = marketability?.providerType === JarvisProviderType.Unity;

    for (const productId in config) {
        const { type, device, option, isUnity } = config[productId];
        const isUnityOrNet = Boolean(isUnity) === addressIsUnity || type === ProductType.NET;

        if (type === selected.type &&
            device === selected.device &&
            option === selected.option &&
            isUnityOrNet
        ) {
            return productId;
        }
    }

    return undefined;
};

const getProduct = (products: JarvisProduct[], id?: string) =>
    products.find((product) => product.id === id);

const getGigakombiOption = (product?: JarvisProduct) => {
    if (!product?.priceAddons.gigakombiDiscountTV) {
        return undefined;
    }

    const [ gigakombiId ] = product.priceAddons.gigakombiDiscountTV.ids;

    return product.options[gigakombiId];
};

/**
 * Selects the container's state from RootState based on the given namespace
 * @param {RootState} state - rootstate of the app
 * @returns {AppState} - the App container's state
 */
export const selectState = (state: RootState) => state[NameSpace.APP] || initialState;

export const selectLoading = createSelector(selectState, state => !state.overlay.isOpen && state.isLoading);
export const selectOverlayLoading = createSelector(selectState, state => state.overlay.isOpen && state.isLoading);
export const selectOverlay = createSelector(selectState, state => state.overlay);
export const selectError = createSelector(selectState, state => Boolean(state.error));
export const selectGigakombi = createSelector(selectState, state => state.gigakombi);
export const selectIsGigakombi = createSelector(selectState, state => state.gigakombi !== Gigakombi.NONE && state.selected.type !== ProductType.NET);
export const selectProducts = createSelector(selectState, state => state.products);
export const selectSelected = createSelector(selectState, state => state.selected);
export const selectProductConfig = createSelector(selectState, state => state.productConfig);
export const selectSelectedId = createSelector(selectProductConfig, selectSelected, selectMarketability, getMetacodeBySelected);
export const selectProduct = createSelector(selectProducts, selectSelectedId, getProduct);
export const selectGigakombiOption = createSelector(selectProduct, getGigakombiOption);

/**
 * Create a structured selector. (Map our selectors into specific keys in an object)
 * Used to connect the state to the container
 */
export const mapStateToProps = createStructuredSelector({
    productConfig: selectProductConfig,
    gigakombi: selectGigakombi,
    isGigakombi: selectIsGigakombi,
    isError: selectError,
    isAppLoading: selectLoading,
    isOverlayLoading: selectOverlayLoading,
    overlay: selectOverlay,
    selected: selectSelected,
    availability: selectAvailabilityResult,
    tvConnect: selectTvConnect,
    tvConnectBv: selectTvConnectBroadvision,
    product: selectProduct,
    customer: selectCustomer,
});
